import React, { useState, useEffect } from 'react';
import { informeService } from '../services/api';
import { 
    FiSettings, 
    FiPlus, 
    FiTrash2, 
    FiCheck, 
    FiClock, 
    FiFileText, 
    FiUploadCloud,
    FiBriefcase,
    FiEdit2,
    FiSave,
    FiX
} from 'react-icons/fi';
import './ConfiguracionPage.css';

const ASEGURADORAS_SUGERIDAS = [
    'Mapfre',
    'Mutua Madrileña',
    'Allianz',
    'AXA',
    'Generali',
    'Santalucía',
    'Pelayo',
    'Catalana Occidente',
    'Reale',
    'Otras'
];

const ConfiguracionPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'plazos' | 'templates'>('plazos');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    // Plazos por Compañía State
    const [plazos, setPlazos] = useState<any[]>([]);
    const [compForm, setCompForm] = useState<{ compania: string; dias: number }>({
        compania: 'Mapfre',
        dias: 5
    });

    // Templates State
    const [templates, setTemplates] = useState<any[]>([]);
    const [templateNombre, setTemplateNombre] = useState<string>('');
    const [templateFile, setTemplateFile] = useState<File | null>(null);
    const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
    const [editingTemplateNombre, setEditingTemplateNombre] = useState<string>('');

    useEffect(() => {
        prvCargarDatos();
    }, [activeTab]);

    const prvCargarDatos = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            if (activeTab === 'plazos') {
                const res = await informeService.obtenerPlazosCompania();
                setPlazos(res.data || []);
            } else {
                const res = await informeService.obtenerTemplates();
                setTemplates(res.data || []);
            }
        } catch (err: any) {
            console.error('Error al cargar datos de configuración:', err);
            setError('Error al cargar la información de configuración');
        } finally {
            setLoading(false);
        }
    };

    const prvShowToast = (msg: string) => {
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    // =========================================================================
    // PLAZOS COMPAÑÍA ACTIONS
    // =========================================================================

    const prvHandleGuardarPlazo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!compForm.compania.trim() || compForm.dias < 1) {
            setError('Especifica un nombre de compañía válido y un mínimo de 1 día de plazo');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await informeService.guardarPlazoCompania({
                compania: compForm.compania.trim(),
                dias_limite_entrega: Number(compForm.dias)
            });
            prvShowToast(`Plazo asignado para ${compForm.compania}`);
            setCompForm({ compania: 'Mapfre', dias: 5 });
            await prvCargarDatos();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al guardar el plazo de la compañía');
        } finally {
            setLoading(false);
        }
    };

    const prvHandleEliminarPlazo = async (id: number, compania: string) => {
        if (!window.confirm(`¿Deseas eliminar la configuración de plazo para ${compania}?`)) return;
        setLoading(true);
        try {
            await informeService.eliminarPlazoCompania(id);
            prvShowToast('Configuración de plazo eliminada');
            await prvCargarDatos();
        } catch (err: any) {
            setError('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    // =========================================================================
    // TEMPLATES ACTIONS
    // =========================================================================

    const prvHandleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.name.endsWith('.docx')) {
                setError('Solo se permiten plantillas en formato Word (.docx)');
                setTemplateFile(null);
                return;
            }
            setTemplateFile(file);
            if (!templateNombre) {
                setTemplateNombre(file.name.replace('.docx', ''));
            }
        }
    };

    const prvHandleUploadTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!templateFile || !templateNombre.trim()) {
            setError('Debes proporcionar un nombre y seleccionar un archivo .docx');
            return;
        }

        setLoading(true);
        setError('');
        const formData = new FormData();
        formData.append('nombre', templateNombre.trim());
        formData.append('file', templateFile);

        try {
            await informeService.subirTemplate(formData);
            setTemplateNombre('');
            setTemplateFile(null);
            prvShowToast('Plantilla Word subida con éxito');
            await prvCargarDatos();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al subir la plantilla');
        } finally {
            setLoading(false);
        }
    };

    const prvHandleDeleteTemplate = async (id: number) => {
        if (!window.confirm('¿Seguro que deseas eliminar esta plantilla?')) return;
        setLoading(true);
        try {
            await informeService.eliminarTemplate(id);
            prvShowToast('Plantilla eliminada');
            await prvCargarDatos();
        } catch (err: any) {
            setError('Error al eliminar la plantilla');
        } finally {
            setLoading(false);
        }
    };

    const prvStartEditingTemplate = (template: any) => {
        setEditingTemplateId(template.id);
        setEditingTemplateNombre(template.nombre);
    };

    const prvCancelEditingTemplate = () => {
        setEditingTemplateId(null);
        setEditingTemplateNombre('');
    };

    const prvSaveTemplateNombre = async (id: number) => {
        if (!editingTemplateNombre.trim()) {
            setError('El nombre de la plantilla no puede estar vacío');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await informeService.actualizarTemplate(id, { nombre: editingTemplateNombre.trim() });
            prvShowToast('Nombre de la plantilla actualizado');
            setEditingTemplateId(null);
            setEditingTemplateNombre('');
            await prvCargarDatos();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al actualizar la plantilla');
        } finally {
            setLoading(false);
        }
    };

    const prvGetCompanyBadgeStyle = (compania: string) => {
        if (!compania || compania === '-') {
            return { backgroundColor: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1' };
        }
        let hash = 0;
        const str = compania.trim().toLowerCase();
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash) % 360;
        return {
            backgroundColor: `hsl(${hue}, 85%, 94%)`,
            color: `hsl(${hue}, 90%, 26%)`,
            border: `1px solid hsl(${hue}, 65%, 78%)`,
            padding: '0.25rem 0.625rem',
            borderRadius: '9999px',
            fontWeight: 600,
            fontSize: '0.8rem',
            display: 'inline-flex',
            alignItems: 'center'
        };
    };

    return (
        <div className="configuracion-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">
                        <span className="title-icon"><FiSettings /></span>
                        Configuración del Sistema
                    </h2>
                    <p className="page-subtitle">
                        Configura los plazos máximos de entrega por compañía aseguradora y tus plantillas de documentos Word (.docx).
                    </p>
                </div>
            </div>

            {error && <div className="alert-error">{error}</div>}
            {successMessage && <div className="toast-success"><FiCheck /> {successMessage}</div>}

            {/* Navigation Tabs */}
            <div className="tabs-nav">
                <button 
                    onClick={() => setActiveTab('plazos')}
                    className={`tab-btn ${activeTab === 'plazos' ? 'active' : ''}`}
                >
                    <FiClock />
                    <span>Plazos de Entrega por Compañía</span>
                </button>
                <button 
                    onClick={() => setActiveTab('templates')}
                    className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
                >
                    <FiFileText />
                    <span>Plantillas de Documentos (.docx)</span>
                </button>
            </div>

            <div className="config-card">
                {activeTab === 'plazos' ? (
                    <div>
                        <h3 className="section-title">
                            <FiBriefcase className="title-icon" />
                            Configurar Días Límite por Aseguradora
                        </h3>

                        {/* Add/Edit Plazo Form */}
                        <form onSubmit={prvHandleGuardarPlazo} className="config-form-card">
                            <div className="form-row-align">
                                <div className="config-form-group" style={{ flex: '2', minWidth: '200px' }}>
                                    <label>Compañía Aseguradora</label>
                                    <input 
                                        type="text" 
                                        list="aseguradoras-list"
                                        placeholder="Ej. Mapfre, Allianz, Mutua Madrileña..." 
                                        value={compForm.compania} 
                                        onChange={(e) => setCompForm({ ...compForm, compania: e.target.value })}
                                        className="config-input"
                                        required
                                    />
                                    <datalist id="aseguradoras-list">
                                        {ASEGURADORAS_SUGERIDAS.map(op => (
                                            <option key={op} value={op} />
                                        ))}
                                    </datalist>
                                </div>
                                <div className="config-form-group" style={{ flex: '1', minWidth: '140px' }}>
                                    <label>Días Límite para Informe</label>
                                    <input 
                                        type="number" 
                                        min="1" 
                                        max="180" 
                                        placeholder="5" 
                                        value={compForm.dias} 
                                        onChange={(e) => setCompForm({ ...compForm, dias: parseInt(e.target.value) || 1 })}
                                        className="config-input"
                                        required
                                    />
                                </div>
                                <button 
                                    type="submit" 
                                    disabled={loading} 
                                    className="btn-primary"
                                >
                                    <FiPlus />
                                    <span>Guardar Plazo</span>
                                </button>
                            </div>
                        </form>

                        {/* Plazos Table */}
                        <div className="config-table-responsive">
                            <table className="config-table">
                                <thead>
                                    <tr>
                                        <th>Compañía Aseguradora</th>
                                        <th style={{ textAlign: 'center', width: '220px' }}>Días Límite de Entrega</th>
                                        <th style={{ textAlign: 'center', width: '100px' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plazos.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                                                No hay plazos de compañía configurados. Define el primero arriba.
                                            </td>
                                        </tr>
                                    ) : (
                                        plazos.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <span style={prvGetCompanyBadgeStyle(item.compania)}>
                                                        {item.compania}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{ 
                                                        backgroundColor: '#eff6ff', 
                                                        color: '#1d4ed8', 
                                                        fontWeight: 600, 
                                                        padding: '0.25rem 0.625rem', 
                                                        borderRadius: '4px',
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {item.dias_limite_entrega} {item.dias_limite_entrega === 1 ? 'día' : 'días'}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button 
                                                        onClick={() => prvHandleEliminarPlazo(item.id, item.compania)}
                                                        className="btn-danger-icon"
                                                        title="Eliminar plazo"
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="templates-grid">
                        {/* List templates */}
                        <div>
                            <h3 className="section-title">
                                <FiFileText className="title-icon" />
                                Plantillas Activas en el Sistema
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {templates.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '8px', border: '2px dashed #cbd5e1' }}>
                                        No se han subido plantillas de documentos Word (.docx). Puedes subir una en el panel lateral.
                                    </div>
                                ) : (
                                    templates.map((template) => (
                                        <div key={template.id} className="template-card-item">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flex: 1 }}>
                                                <div style={{ 
                                                    width: '40px', 
                                                    height: '40px', 
                                                    borderRadius: '8px', 
                                                    backgroundColor: '#eff6ff', 
                                                    color: '#2563eb',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1.25rem',
                                                    flexShrink: 0
                                                }}>
                                                    <FiFileText />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    {editingTemplateId === template.id ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input 
                                                                type="text" 
                                                                value={editingTemplateNombre}
                                                                onChange={(e) => setEditingTemplateNombre(e.target.value)}
                                                                className="config-input"
                                                                style={{ padding: '0.35rem 0.5rem', fontSize: '0.875rem' }}
                                                                autoFocus
                                                            />
                                                            <button 
                                                                onClick={() => prvSaveTemplateNombre(template.id)}
                                                                className="btn-primary"
                                                                style={{ padding: '0.35rem 0.6rem' }}
                                                                title="Guardar nombre"
                                                            >
                                                                <FiSave size={15} />
                                                            </button>
                                                            <button 
                                                                onClick={prvCancelEditingTemplate}
                                                                className="btn-danger-icon"
                                                                title="Cancelar"
                                                            >
                                                                <FiX size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{template.nombre}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{template.docx_filename}</div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {editingTemplateId !== template.id && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                    <button 
                                                        onClick={() => prvStartEditingTemplate(template)}
                                                        className="btn-danger-icon"
                                                        style={{ color: '#2563eb' }}
                                                        title="Editar nombre de plantilla"
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => prvHandleDeleteTemplate(template.id)}
                                                        className="btn-danger-icon"
                                                        title="Eliminar plantilla"
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Upload template form */}
                        <div className="config-form-card" style={{ marginBottom: 0 }}>
                            <h3 className="section-title">
                                <FiUploadCloud className="title-icon" />
                                Subir Plantilla (.docx)
                            </h3>
                            <form onSubmit={prvHandleUploadTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div className="config-form-group">
                                    <label>Nombre de la Plantilla</label>
                                    <input 
                                        type="text" 
                                        placeholder="Ej: Plantilla Oficial Hogar"
                                        value={templateNombre} 
                                        onChange={(e) => setTemplateNombre(e.target.value)}
                                        className="config-input"
                                        required
                                    />
                                </div>
                                <div className="config-form-group">
                                    <label>Archivo Word (.docx)</label>
                                    <div className="file-drop-container">
                                        <input 
                                            type="file" 
                                            accept=".docx"
                                            onChange={prvHandleFileChange}
                                            className="file-drop-input"
                                            required
                                        />
                                        <div style={{ fontSize: '2rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                                            <FiUploadCloud style={{ margin: '0 auto' }} />
                                        </div>
                                        {templateFile ? (
                                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#16a34a', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {templateFile.name}
                                            </span>
                                        ) : (
                                            <>
                                                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a', display: 'block' }}>
                                                    Haz clic o arrastra una plantilla Word
                                                </span>
                                                <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '0.25rem' }}>
                                                    Formatos permitidos: .docx
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    type="submit" 
                                    disabled={loading || !templateFile} 
                                    className="btn-primary"
                                    style={{ width: '100%', marginTop: '0.5rem' }}
                                >
                                    <FiUploadCloud />
                                    <span>Subir Plantilla</span>
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConfiguracionPage;
